import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { BRANCHES } from '@/mocks/mockData';

interface AktifDers {
  id: string;
  ogrenci_id: string;
  ogretmen_id: string;
  brans: string | null;
  ders_tarihi: string;
  ders_saati: string | null;
  ders_suresi: number | null;
  islenen_konu: string | null;
  odev_verildi: boolean | null;
  odev_aciklamasi: string | null;
  mini_sinav: boolean | null;
  sinav_sonucu: string | null;
  ogrenci_performans_notu: string | null;
  tekrar_konusu: string | null;
  veli_bilgilendirildi: boolean | null;
  ders_durumu: string | null;
  odeme_durumu: string | null;
  ek_aciklama: string | null;
  ogrenci?: { ogrenci_adi: string };
  ogretmen?: { ad_soyad: string };
}

const statusColors: Record<string, string> = {
  Yapıldı: 'bg-green-100 text-green-700',
  Ertelendi: 'bg-yellow-100 text-yellow-700',
  İptal: 'bg-red-100 text-red-700',
  'Öğrenci Katılmadı': 'bg-orange-100 text-orange-700',
};

const payColors: Record<string, string> = {
  Ödendi: 'bg-green-100 text-green-700',
  Bekliyor: 'bg-yellow-100 text-yellow-700',
  Eksik: 'bg-red-100 text-red-700',
};

const emptyForm = {
  studentId: '', branch: '', date: '', time: '', duration: '60',
  topic: '', homeworkGiven: false, homeworkDesc: '', quizDone: false,
  quizResult: '', performanceNote: '', repeatTopic: '', parentInformed: false,
  status: 'Yapıldı', note: '',
};

export default function AktifDerslerPage() {
  const [data, setData] = useState<AktifDers[]>([]);
  const [students, setStudents] = useState<{id: string; ogrenci_adi: string; branslar: string[] | null; atanan_ogretmen_id: string | null}[]>([]);
  const [teachers, setTeachers] = useState<{id: string; ad_soyad: string}[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterTeacher, setFilterTeacher] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editLesson, setEditLesson] = useState<AktifDers | null>(null);
  const [deleteLesson, setDeleteLesson] = useState<AktifDers | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const handleDeleteLesson = async () => {
    if (!deleteLesson) return;
    setSaving(true);
    await supabase.from('egtpanel_aktif_dersler').delete().eq('id', deleteLesson.id);
    setSaving(false);
    setDeleteLesson(null);
    fetchData();
  };

  const handleEditSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editLesson) return;
    setSaving(true);
    const fd = new FormData(e.currentTarget);
    await supabase.from('egtpanel_aktif_dersler').update({
      ders_durumu: fd.get('status') as string,
      islenen_konu: fd.get('topic') as string || null,
      odev_verildi: fd.get('homework') === 'true',
      ders_suresi: Number(fd.get('duration')) || null,
      ogrenci_performans_notu: fd.get('performance') as string || null,
      ek_aciklama: fd.get('note') as string || null,
      guncelleme_tarihi: new Date().toISOString(),
    }).eq('id', editLesson.id);
    setSaving(false);
    setEditLesson(null);
    showToast('Ders kaydı güncellendi!');
    fetchData();
  };

  const fetchData = useCallback(async () => {
    const [{ data: rows }, { data: s }, { data: t }] = await Promise.all([
      supabase.from('egtpanel_aktif_dersler')
        .select('*, ogrenci:egtpanel_ogrenciler(ogrenci_adi), ogretmen:egtpanel_ogretmenler(ad_soyad)')
        .order('ders_tarihi', { ascending: false }),
      supabase.from('egtpanel_ogrenciler').select('id, ogrenci_adi, branslar, atanan_ogretmen_id').eq('aktif', true).order('ogrenci_adi'),
      supabase.from('egtpanel_ogretmenler').select('id, ad_soyad').order('ad_soyad'),
    ]);
    setData(rows || []);
    setStudents(s || []);
    setTeachers(t || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = data.filter(d => {
    const q = search.toLowerCase();
    const ogrAdi = d.ogrenci?.ogrenci_adi?.toLowerCase() || '';
    const ogrTAdi = d.ogretmen?.ad_soyad?.toLowerCase() || '';
    const konuLow = d.islenen_konu?.toLowerCase() || '';
    const matchQ = !q || ogrAdi.includes(q) || ogrTAdi.includes(q) || konuLow.includes(q);
    const matchTeacher = !filterTeacher || d.ogretmen_id === filterTeacher;
    const matchStatus = !filterStatus || d.ders_durumu === filterStatus;
    return matchQ && matchTeacher && matchStatus;
  });

  const selectedStudent = students.find(s => s.id === form.studentId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.studentId || !form.date || !form.topic) return;
    setSaving(true);
    const ogretmenId = selectedStudent?.atanan_ogretmen_id || null;
    await supabase.from('egtpanel_aktif_dersler').insert({
      ogrenci_id: form.studentId,
      ogretmen_id: ogretmenId,
      brans: form.branch || null,
      ders_tarihi: form.date,
      ders_saati: form.time || null,
      ders_suresi: Number(form.duration) || null,
      islened_konu: form.topic,
      islenen_konu: form.topic,
      odev_verildi: form.homeworkGiven,
      odev_aciklamasi: form.homeworkDesc || null,
      mini_sinav: form.quizDone,
      sinav_sonucu: form.quizResult || null,
      ogrenci_performans_notu: form.performanceNote || null,
      tekrar_konusu: form.repeatTopic || null,
      veli_bilgilendirildi: form.parentInformed,
      ders_durumu: form.status,
      odeme_durumu: 'Bekliyor',
      ek_aciklama: form.note || null,
    });
    setSaving(false);
    setShowModal(false);
    setForm(emptyForm);
    fetchData();
  };

  const today = new Date().toISOString().split('T')[0];

  if (loading) return (
    <div className="p-6 flex items-center justify-center py-32">
      <i className="ri-loader-4-line text-3xl text-slate-300 animate-spin" />
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Aktif Dersler</h1>
          <p className="text-slate-500 text-sm mt-1">Yapılan dersleri kaydedin ve takip edin</p>
        </div>
        <button onClick={() => setShowModal(true)} className="flex items-center gap-2 bg-[#1e3a5f] text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-[#162d4a] transition-colors whitespace-nowrap cursor-pointer">
          <i className="ri-add-line" /> Ders Gir
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Toplam', val: data.length, color: 'text-slate-700', bg: 'bg-slate-50' },
          { label: 'Yapıldı', val: data.filter(d => d.ders_durumu === 'Yapıldı').length, color: 'text-green-700', bg: 'bg-green-50' },
          { label: 'Ödeme Bekliyor', val: data.filter(d => d.odeme_durumu === 'Bekliyor').length, color: 'text-amber-700', bg: 'bg-amber-50' },
          { label: 'Bugün', val: data.filter(d => d.ders_tarihi === today).length, color: 'text-blue-700', bg: 'bg-blue-50' },
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-xl p-4`}>
            <p className="text-xs text-slate-500 font-medium">{s.label}</p>
            <p className={`text-2xl font-bold mt-1 ${s.color}`}>{s.val}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-100 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Öğrenci, öğretmen veya konu ara..." className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
          </div>
          <select value={filterTeacher} onChange={e => setFilterTeacher(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
            <option value="">Tüm Öğretmenler</option>
            {teachers.map(t => <option key={t.id} value={t.id}>{t.ad_soyad}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
            <option value="">Tüm Durumlar</option>
            {['Yapıldı', 'Ertelendi', 'İptal', 'Öğrenci Katılmadı'].map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                {['Tarih / Saat', 'Öğrenci', 'Öğretmen', 'Branş', 'Süre', 'Konu', 'Ödev', 'Ders Durumu', 'Ödeme', ''].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filtered.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-16">
                  <i className="ri-book-open-line text-4xl text-slate-200 block mb-2" />
                  <p className="text-slate-400 text-sm">Ders kaydı bulunamadı</p>
                </td></tr>
              ) : filtered.map(d => (
                <tr key={d.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="font-medium text-slate-800">{d.ders_tarihi}</div>
                    <div className="text-xs text-slate-400">{d.ders_saati?.slice(0,5) || '—'}</div>
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-800 whitespace-nowrap">{d.ogrenci?.ogrenci_adi || '—'}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{d.ogretmen?.ad_soyad || '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full text-xs font-medium">{d.brans || '—'}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{d.ders_suresi ? `${d.ders_suresi} dk` : '—'}</td>
                  <td className="px-4 py-3 text-slate-600 max-w-[150px] truncate">{d.islenen_konu || '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {d.odev_verildi
                      ? <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded-full text-xs">Verildi</span>
                      : <span className="bg-slate-50 text-slate-400 px-2 py-0.5 rounded-full text-xs">Yok</span>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[d.ders_durumu || ''] || 'bg-slate-100 text-slate-500'}`}>{d.ders_durumu || '—'}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${payColors[d.odeme_durumu || ''] || 'bg-slate-100 text-slate-500'}`}>{d.odeme_durumu || '—'}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <button onClick={() => setEditLesson(d)} className="text-slate-600 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors cursor-pointer"><i className="ri-edit-line" /></button>
                      <button onClick={() => setDeleteLesson(d)} className="text-red-500 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-red-100 hover:bg-red-50 transition-colors cursor-pointer"><i className="ri-delete-bin-line" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {toast && <div className="fixed top-4 right-4 z-50 bg-[#1e3a5f] text-white px-5 py-3 rounded-xl text-sm font-medium">{toast}</div>}

      {/* Edit Lesson Modal */}
      {editLesson && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <div>
                <h2 className="text-lg font-bold text-slate-800">Ders Kaydını Düzenle</h2>
                <p className="text-sm text-slate-500">{editLesson.ogrenci?.ogrenci_adi} · {editLesson.ders_tarihi}</p>
              </div>
              <button onClick={() => setEditLesson(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer"><i className="ri-close-line" /></button>
            </div>
            <form onSubmit={handleEditSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Ders Durumu</label>
                  <select name="status" defaultValue={editLesson.ders_durumu || 'Yapıldı'} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none">
                    {['Yapıldı', 'Ertelendi', 'İptal', 'Öğrenci Katılmadı'].map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Süre (dk)</label>
                  <select name="duration" defaultValue={editLesson.ders_suresi || 60} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none">
                    {[30,40,45,60,90].map(d => <option key={d} value={d}>{d} dk</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">İşlenen Konu</label>
                <input type="text" name="topic" defaultValue={editLesson.islenen_konu || ''} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none" />
              </div>
              <div className="flex items-center justify-between">
                <label className="text-sm text-slate-600">Ödev verildi mi?</label>
                <select name="homework" defaultValue={editLesson.odev_verildi ? 'true' : 'false'} className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm focus:outline-none">
                  <option value="true">Evet</option>
                  <option value="false">Hayır</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Performans Notu</label>
                <input type="text" name="performance" defaultValue={editLesson.ogrenci_performans_notu || ''} placeholder="İyi, Orta, Gelişiyor..." className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Ek Açıklama</label>
                <textarea name="note" defaultValue={editLesson.ek_aciklama || ''} rows={2} maxLength={500} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none resize-none" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setEditLesson(null)} className="flex-1 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 cursor-pointer">İptal</button>
                <button type="submit" disabled={saving} className="flex-1 py-2.5 rounded-lg bg-[#1e3a5f] text-white text-sm font-medium hover:bg-[#162d4a] cursor-pointer">{saving ? <i className="ri-loader-4-line animate-spin" /> : 'Kaydet'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteLesson && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-xl mb-4"><i className="ri-delete-bin-line text-red-500 text-xl" /></div>
            <h3 className="font-bold text-slate-800 text-lg mb-1">Ders Kaydını Sil</h3>
            <p className="text-slate-500 text-sm mb-6"><strong>{deleteLesson.ogrenci?.ogrenci_adi}</strong> - {deleteLesson.ders_tarihi} ders kaydını silmek istediğinizden emin misiniz?</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteLesson(null)} className="flex-1 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 cursor-pointer">İptal</button>
              <button onClick={handleDeleteLesson} disabled={saving} className="flex-1 py-2.5 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 disabled:opacity-60 cursor-pointer">{saving ? 'Siliniyor...' : 'Evet, Sil'}</button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <h2 className="text-lg font-bold text-slate-800">Ders Girişi</h2>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer">
                <i className="ri-close-line" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Öğrenci Seç *</label>
                  <select required value={form.studentId} onChange={e => setForm(p => ({...p, studentId: e.target.value, branch: ''}))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                    <option value="">— Seçiniz —</option>
                    {students.map(s => <option key={s.id} value={s.id}>{s.ogrenci_adi}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Branş *</label>
                  <select required value={form.branch} onChange={e => setForm(p => ({...p, branch: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                    <option value="">— Seçiniz —</option>
                    {(selectedStudent?.branslar?.length ? selectedStudent.branslar : BRANCHES).map(b => <option key={b}>{b}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Ders Tarihi *</label>
                  <input required type="date" value={form.date} onChange={e => setForm(p => ({...p, date: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Ders Saati</label>
                  <input type="time" value={form.time} onChange={e => setForm(p => ({...p, time: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Ders Süresi (dk)</label>
                  <select value={form.duration} onChange={e => setForm(p => ({...p, duration: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                    {['30', '40', '45', '60', '90'].map(d => <option key={d} value={d}>{d} dk</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Ders Durumu</label>
                  <select value={form.status} onChange={e => setForm(p => ({...p, status: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                    {['Yapıldı', 'Ertelendi', 'İptal', 'Öğrenci Katılmadı'].map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">İşlenen Konu *</label>
                <input required value={form.topic} onChange={e => setForm(p => ({...p, topic: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
              </div>
              <div className="flex flex-wrap gap-6">
                {[
                  { label: 'Ödev verildi mi?', key: 'homeworkGiven', val: form.homeworkGiven },
                  { label: 'Mini sınav yapıldı mı?', key: 'quizDone', val: form.quizDone },
                  { label: 'Veliye bilgi verildi mi?', key: 'parentInformed', val: form.parentInformed },
                ].map(f => (
                  <label key={f.key} className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={f.val} onChange={e => setForm(p => ({...p, [f.key]: e.target.checked}))} className="rounded" />
                    <span className="text-sm text-slate-700">{f.label}</span>
                  </label>
                ))}
              </div>
              {form.homeworkGiven && (
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Ödev Açıklaması</label>
                  <input value={form.homeworkDesc} onChange={e => setForm(p => ({...p, homeworkDesc: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Öğrenci Performans Notu</label>
                  <input value={form.performanceNote} onChange={e => setForm(p => ({...p, performanceNote: e.target.value}))} placeholder="İyi, Orta, Gelişiyor..." className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Tekrar Gerekli Konu</label>
                  <input value={form.repeatTopic} onChange={e => setForm(p => ({...p, repeatTopic: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Ek Açıklama</label>
                <textarea value={form.note} onChange={e => setForm(p => ({...p, note: e.target.value}))} rows={2} maxLength={500} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20 resize-none" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50">İptal</button>
                <button type="submit" disabled={saving} className="flex-1 py-2.5 rounded-lg bg-[#1e3a5f] text-white text-sm font-medium hover:bg-[#162d4a]">
                  {saving ? <i className="ri-loader-4-line animate-spin" /> : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}